/**
 * When the invites' copy goes (decision 75, decision 39's timing; part 38b log, DI6): a few seconds after a save of a
 * shared book's chapter (a save is itself a pause in typing), at once when the page is hidden or the device comes back
 * online, and as the book opens. Saves within the pause wait together for one reconcile. Nothing here runs on a
 * keystroke: the workspace tells it of saves only. The timers are injected, so it runs in tests.
 */

/** How long after a save the copy waits for more typing (decision 39's "a few seconds"). */
export const PUSH_PAUSE_MS = 4000;

/**
 * Fix-next 67, UR2: the first wait before an unsent invite is sent again after its making didn't reach the relay (the
 * network or a server error, while online too); each next wait twice the one before, up to {@link UNSENT_RETRY_MAX_MS}.
 * Each try is a list and a making (two requests), so at most about a dozen an hour.
 */
export const UNSENT_RETRY_MS = 15_000;
export const UNSENT_RETRY_MAX_MS = 300_000;

/** The timers it runs on (`setTimeout`, `clearTimeout` in the app). */
export interface PushTimers {
  set(run: () => void, ms: number): number;
  clear(handle: number): void;
}

/** DI6: the copy's schedule, over `reconcile` (the invites' reconcile of one book). */
export class CopySchedule {
  readonly #reconcile: (bookId: string) => Promise<unknown>;
  readonly #timers: PushTimers;
  #pending: { readonly bookId: string; readonly handle: number } | null = null;
  /** UR2: each book's next try of an unsent invite, and how many tries it has had. */
  readonly #retries = new Map<string, { readonly handle: number; readonly tries: number }>();

  constructor(reconcile: (bookId: string) => Promise<unknown>, timers: PushTimers) {
    this.#reconcile = reconcile;
    this.#timers = timers;
  }

  /** A save of book `bookId`'s open chapter: its reconcile waits {@link PUSH_PAUSE_MS} for the next save, if any. */
  saved(bookId: string): void {
    const pending = this.#pending;
    // Another book's wait goes now: the book open has changed.
    if (pending !== null && pending.bookId !== bookId) this.flush();
    if (this.#pending !== null) this.#timers.clear(this.#pending.handle);
    this.#pending = {
      bookId,
      handle: this.#timers.set(() => {
        this.flush();
      }, PUSH_PAUSE_MS),
    };
  }

  /** The page hidden, or the wait over: the waiting reconcile starts now; nothing when none waits. */
  flush(): void {
    const pending = this.#pending;
    if (pending === null) return;
    this.#pending = null;
    this.#timers.clear(pending.handle);
    void this.#reconcile(pending.bookId).catch(() => undefined);
  }

  /**
   * Fix-next 67, UR2: book `bookId` has an unsent invite whose making didn't reach the relay (`unsent`): its reconcile
   * runs again after the next wait (one at a time; a run's own answer sets the next); none
   * (sent, refused, nothing unsent, signed out): the wait goes, and the waits start over.
   */
  retry(bookId: string, unsent: boolean): void {
    const held = this.#retries.get(bookId);
    if (held !== undefined) this.#timers.clear(held.handle);
    this.#retries.delete(bookId);
    if (!unsent) return;
    const tries = held === undefined ? 0 : held.tries + 1;
    const wait = Math.min(UNSENT_RETRY_MS * 2 ** tries, UNSENT_RETRY_MAX_MS);
    const handle = this.#timers.set(() => {
      // The run's answer sets the next wait; the count is kept until then.
      this.#retries.set(bookId, { handle: -1, tries });
      void this.#reconcile(bookId).catch(() => undefined);
    }, wait);
    this.#retries.set(bookId, { handle, tries });
  }

  /** Book `bookId` opened, or the device back online with it open: its reconcile starts now, any wait for it with it. */
  now(bookId: string): void {
    if (this.#pending?.bookId === bookId) {
      this.flush();
      return;
    }
    void this.#reconcile(bookId).catch(() => undefined);
  }
}
